import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import './Starfield.scss';

/**
 * Stars orbit the centre of the canvas rather than drifting in a straight
 * line: `angle` advances every frame and the screen position is derived from
 * it. Depth (`z`) drives radius, brightness and angular speed together, so
 * distant stars are small, dim and slow. Polar motion also means nothing ever
 * has to wrap across an edge, which is where drifting starfields tend to pop.
 *
 * The field is also interactive: the cursor acts as a small gravity well that
 * draws nearby stars in, brightens them and joins them into constellations,
 * and a click launches a meteor along a path that runs through the point that
 * was clicked.
 */
interface Star {
  angle: number;
  distance: number;
  size: number;
  spin: number;
  phase: number;
  twinkle: number;
  brightness: number;
  sprite: number;
}

interface Shooter {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  ttl: number;
}

/** A 3x phone would otherwise rasterise 9x the pixels for no visible gain. */
const DPR_CAP = 2;
const MAX_STARS = 260;
const AREA_PER_STAR = 8000;
const SPRITE_PX = 64;
/** Clamps the delta after a tab stall so nothing teleports on the next frame. */
const MAX_FRAME_MS = 50;
const SHOOTER_MIN_WIDTH = 640;
const RESIZE_DEBOUNCE_MS = 150;

// --- Pointer interaction ---------------------------------------------------

/** Reach of the cursor's pull, in CSS pixels. */
const LENS_RADIUS = 200;
/** How far the nearest star is drawn towards the cursor, in CSS pixels. */
const LENS_PULL = 16;
/** Stars closer than this to each other inside the lens get joined up. */
const LINK_DISTANCE = 118;
/** Fraction of the remaining distance the lens closes each frame. */
const LENS_EASE = 0.12;
/** A tap should not leave a permanent lens sitting where the finger was. */
const FINE_POINTER = '(pointer: fine)';
/** Speed of a meteor launched by a click, in CSS pixels per second. */
const SUMMONED_SPEED = 900;

/** mulberry32 — small, fast, and even enough for scattering points. */
const createRandom = (seed: number) => {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const readToken = (name: string, fallback: string): string => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();

  return value || fallback;
};

const toRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((char) => char + char)
          .join('')
      : clean;
  const value = Number.parseInt(full, 16);

  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

/**
 * Stars are drawn as a pre-rendered sprite rather than an arc with
 * `shadowBlur`. Per-star shadow blur is the single most expensive thing you
 * can do in a 2D loop and it is what makes hand-rolled starfields stutter on
 * mid-range phones.
 */
const makeSprite = (hex: string): HTMLCanvasElement => {
  const sprite = document.createElement('canvas');
  sprite.width = SPRITE_PX;
  sprite.height = SPRITE_PX;

  const context = sprite.getContext('2d');
  if (!context) return sprite;

  const [r, g, b] = toRgb(hex);
  const half = SPRITE_PX / 2;
  const gradient = context.createRadialGradient(half, half, 0, half, half, half);

  gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
  gradient.addColorStop(0.12, `rgba(${r}, ${g}, ${b}, 0.88)`);
  gradient.addColorStop(0.34, `rgba(${r}, ${g}, ${b}, 0.24)`);
  gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

  context.fillStyle = gradient;
  context.fillRect(0, 0, SPRITE_PX, SPRITE_PX);

  return sprite;
};

export const Starfield = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  /**
   * A fresh sky on every page load, but fixed for the life of this mount.
   * `build()` re-runs on resize, so drawing straight from `Math.random()` there
   * would scatter an entirely new field every time the window changed size or
   * a phone rotated.
   */
  const [starSeed] = useState(() => (Math.random() * 0xffffffff) >>> 0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const host = canvas.parentElement ?? canvas;

    const sprites = [
      makeSprite(readToken('--color-text', '#dde3e9')),
      makeSprite(readToken('--color-muted', '#76828e')),
      makeSprite(readToken('--color-accent', '#db5227')),
    ];
    const [tr, tg, tb] = toRgb(readToken('--color-accent', '#db5227'));
    const [lr, lg, lb] = toRgb(readToken('--color-text', '#dde3e9'));

    let width = 0;
    let height = 0;
    let centreX = 0;
    let centreY = 0;
    let originX = 0;
    let originY = 0;
    let stars: Star[] = [];
    let shooters: Shooter[] = [];
    let shootersEnabled = false;
    let nextShooter = 0;
    let elapsed = 0;
    let frame = 0;
    let lastTime = 0;
    let resizeTimer = 0;

    // Where the cursor is, and where the lens has eased to. They are separate
    // so the distortion trails the pointer instead of snapping to it.
    let pointerX = 0;
    let pointerY = 0;
    let pointerInside = false;
    let lensX = 0;
    let lensY = 0;
    let lensStrength = 0;
    /** Stars inside the lens this frame. Cleared and refilled, never realloc'd. */
    const near: { x: number; y: number; weight: number }[] = [];

    const build = () => {
      const rect = host.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      centreX = width / 2;
      centreY = height / 2;
      // The host fills the viewport and the page does not scroll, so caching
      // the offset keeps `pointermove` off the layout path entirely.
      originX = rect.left;
      originY = rect.top;

      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      // Draw in CSS pixels; the transform handles the device scale.
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Stars fill the disc that circumscribes the viewport, so the corners
      // stay populated as the field rotates.
      const maxDistance = Math.hypot(width, height) / 2;
      const count = Math.min(
        MAX_STARS,
        Math.round((Math.PI * maxDistance * maxDistance) / AREA_PER_STAR)
      );

      // Re-seeded from the same per-mount seed on every build, so a resize
      // rescales this session's sky rather than scattering a new one.
      const random = createRandom(starSeed);

      stars = Array.from({ length: count }, () => {
        const z = random();
        const roll = random();

        return {
          angle: random() * Math.PI * 2,
          // sqrt keeps the disc evenly covered instead of clumping at the centre.
          distance: Math.sqrt(random()) * maxDistance,
          size: 5 + z * 14,
          spin: 0.006 + z * 0.013,
          phase: random() * Math.PI * 2,
          twinkle: 0.5 + random() * 1.5,
          brightness: 0.36 + z * 0.5,
          sprite: roll < 0.05 ? 2 : roll < 0.45 ? 1 : 0,
        };
      });

      shooters = [];
      shootersEnabled = width >= SHOOTER_MIN_WIDTH;
      nextShooter = 2 + Math.random() * 5;
    };

    const spawnShooter = () => {
      // Heading roughly 27°–63° below the horizontal, entering from the top.
      const heading = Math.PI * (0.15 + Math.random() * 0.2);
      const speed = 420 + Math.random() * 280;

      shooters.push({
        x: Math.random() * width * 1.1 - width * 0.15,
        y: -30,
        vx: Math.cos(heading) * speed,
        vy: Math.sin(heading) * speed,
        age: 0,
        ttl: 1.2,
      });
    };

    /**
     * A meteor the visitor asked for: it is placed off the top edge and aimed
     * so that its path runs exactly through the point that was clicked.
     */
    const summonShooter = (targetX: number, targetY: number) => {
      const heading = Math.PI * (0.18 + Math.random() * 0.24);
      const dirX = Math.cos(heading);
      const dirY = Math.sin(heading);
      // Distance back along the heading that puts the start just off-screen.
      const lead = (targetY + 40) / dirY;

      shooters.push({
        x: targetX - dirX * lead,
        y: -40,
        vx: dirX * SUMMONED_SPEED,
        vy: dirY * SUMMONED_SPEED,
        age: 0,
        // Long enough to cross the rest of the viewport after the target.
        ttl: (lead + height) / SUMMONED_SPEED + 0.3,
      });
    };

    const draw = (delta: number) => {
      elapsed += delta;
      context.clearRect(0, 0, width, height);

      // Easing the lens position (and its strength, so it fades in and out
      // rather than blinking) keeps the distortion feeling like mass being
      // dragged through the field.
      lensX += (pointerX - lensX) * LENS_EASE;
      lensY += (pointerY - lensY) * LENS_EASE;
      lensStrength += ((pointerInside ? 1 : 0) - lensStrength) * LENS_EASE;

      const lensActive = lensStrength > 0.01;
      near.length = 0;

      for (const star of stars) {
        star.angle += star.spin * delta;

        let x = centreX + Math.cos(star.angle) * star.distance;
        let y = centreY + Math.sin(star.angle) * star.distance;
        let alpha =
          star.brightness + 0.28 * Math.sin(elapsed * star.twinkle + star.phase);

        if (lensActive) {
          const dx = lensX - x;
          const dy = lensY - y;
          const distance = Math.hypot(dx, dy);

          if (distance < LENS_RADIUS && distance > 0.001) {
            // 1 at the cursor, 0 at the rim, eased so there is no hard edge
            // where the effect starts.
            const falloff = (1 - distance / LENS_RADIUS) ** 2 * lensStrength;

            x += (dx / distance) * LENS_PULL * falloff;
            y += (dy / distance) * LENS_PULL * falloff;
            alpha += 0.5 * falloff;

            near.push({ x, y, weight: falloff });
          }
        }

        context.globalAlpha = Math.max(0, Math.min(1, alpha));
        context.drawImage(
          sprites[star.sprite],
          x - star.size / 2,
          y - star.size / 2,
          star.size,
          star.size
        );
      }

      context.globalAlpha = 1;

      // Constellations. Only stars already inside the lens are considered, so
      // the pair loop runs over a handful of points rather than all 260.
      if (near.length > 1) {
        context.lineWidth = 1;

        for (let i = 0; i < near.length; i += 1) {
          for (let j = i + 1; j < near.length; j += 1) {
            const a = near[i];
            const b = near[j];
            const span = Math.hypot(a.x - b.x, a.y - b.y);
            if (span > LINK_DISTANCE) continue;

            const alpha =
              (1 - span / LINK_DISTANCE) * Math.min(a.weight, b.weight) * 0.8;
            if (alpha < 0.01) continue;

            context.strokeStyle = `rgba(${lr}, ${lg}, ${lb}, ${alpha.toFixed(3)})`;
            context.beginPath();
            context.moveTo(a.x, a.y);
            context.lineTo(b.x, b.y);
            context.stroke();
          }
        }
      }

      if (shootersEnabled) {
        nextShooter -= delta;
        if (nextShooter <= 0) {
          nextShooter = 5 + Math.random() * 9;
          spawnShooter();
        }
      }

      context.lineWidth = 1.6;
      context.lineCap = 'round';

      for (let i = shooters.length - 1; i >= 0; i -= 1) {
        const shooter = shooters[i];
        shooter.age += delta;
        shooter.x += shooter.vx * delta;
        shooter.y += shooter.vy * delta;

        if (
          shooter.age > shooter.ttl ||
          shooter.y > height + 60 ||
          shooter.x > width + 60
        ) {
          shooters.splice(i, 1);
          continue;
        }

        const fade = 1 - shooter.age / shooter.ttl;
        const tailX = shooter.x - shooter.vx * 0.055;
        const tailY = shooter.y - shooter.vy * 0.055;
        const trail = context.createLinearGradient(
          shooter.x,
          shooter.y,
          tailX,
          tailY
        );

        trail.addColorStop(0, `rgba(${tr}, ${tg}, ${tb}, ${0.9 * fade})`);
        trail.addColorStop(1, `rgba(${tr}, ${tg}, ${tb}, 0)`);

        context.strokeStyle = trail;
        context.beginPath();
        context.moveTo(shooter.x, shooter.y);
        context.lineTo(tailX, tailY);
        context.stroke();
      }
    };

    const tick = (now: number) => {
      const delta = Math.min(now - lastTime, MAX_FRAME_MS) / 1000;
      lastTime = now;
      draw(delta);
      frame = requestAnimationFrame(tick);
    };

    const start = () => {
      if (frame) return;
      lastTime = performance.now();
      frame = requestAnimationFrame(tick);
    };

    const stop = () => {
      if (!frame) return;
      cancelAnimationFrame(frame);
      frame = 0;
    };

    // No point burning battery animating a field nobody can see.
    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else start();
    };

    // A touch pointer has no hover state, so the lens would stick wherever the
    // last tap landed. Meteors are still summonable there — see `onPointerDown`.
    const finePointer = window.matchMedia(FINE_POINTER).matches;

    const onPointerMove = (event: PointerEvent) => {
      if (!finePointer) return;
      pointerX = event.clientX - originX;
      pointerY = event.clientY - originY;

      if (!pointerInside) {
        // Jump the lens to the entry point instead of sweeping it across the
        // whole field from wherever it was last parked.
        lensX = pointerX;
        lensY = pointerY;
        pointerInside = true;
      }
    };

    const onPointerLeave = () => {
      pointerInside = false;
    };

    /** Click anywhere and a meteor is launched through that point. */
    const onPointerDown = (event: PointerEvent) => {
      const x = event.clientX - originX;
      const y = event.clientY - originY;
      if (x < 0 || y < 0 || x > width || y > height) return;

      summonShooter(x, y);
      start(); // in case a hidden tab parked the loop mid-gesture
    };


    const observer = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        build();
        // Resizing a canvas clears it, so always repaint. Waiting for the next
        // animation frame leaves the field blank whenever rAF is throttled —
        // an occluded window does that without firing `visibilitychange`.
        draw(0);
      }, RESIZE_DEBOUNCE_MS);
    });

    build();
    draw(0);

    // Every pointer effect needs the loop to repaint, so under reduced motion
    // none of it is wired up at all — a summoned meteor with no frames to run
    // in would just queue up forever.
    if (!prefersReducedMotion) {
      start();
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerdown', onPointerDown, { passive: true });
      window.addEventListener('blur', onPointerLeave);
      document.addEventListener('pointerleave', onPointerLeave);
    }

    observer.observe(host);

    return () => {
      stop();
      observer.disconnect();
      window.clearTimeout(resizeTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('blur', onPointerLeave);
      document.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [prefersReducedMotion, starSeed]);

  return <canvas className="starfield" ref={canvasRef} aria-hidden="true" />;
};
